import { useState, useEffect } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  FileText,
  Search,
  User,
  Calendar,
  Mail,
  Phone,
  Building,
  ChevronRight,
  Download,
  CheckCircle,
  MessageSquare,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminQuizResponsesPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [submissionsRes, quizRes] = await Promise.all([
        axios.get(`${API}/admin/quiz/submissions`),
        axios.get(`${API}/quiz/default`)
      ]);
      setSubmissions(submissionsRes.data || []);
      setQuiz(quizRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle risposte");
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter(s =>
    s.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.centro_estetico?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getQuestionText = (questionId) => {
    if (!quiz) return questionId;
    const question = quiz.questions.find(q => q.question_id === questionId);
    return question?.question_text || questionId;
  };

  const getQuestionOptions = (questionId) => {
    if (!quiz) return [];
    const question = quiz.questions.find(q => q.question_id === questionId);
    return question?.options || [];
  };

  const getQuestionType = (questionId) => {
    if (!quiz) return "multiple_choice";
    const question = quiz.questions.find(q => q.question_id === questionId);
    return question?.question_type || "multiple_choice";
  };

  const getAnswerText = (questionId, answer) => {
    const options = getQuestionOptions(questionId);
    if (options.length > 0) {
      const option = options.find(o => o.startsWith(answer + ")"));
      return option || answer;
    }
    return answer;
  };

  const exportToCSV = () => {
    if (!quiz || submissions.length === 0) return;
    
    // Build CSV header
    const headers = ["Nome", "Email", "Telefono", "Centro Estetico", "Data Invio"];
    quiz.questions.forEach(q => {
      headers.push(q.question_text.substring(0, 50));
    });
    
    // Build CSV rows
    const rows = submissions.map(s => {
      const row = [
        s.user_name,
        s.user_email,
        s.user_phone || "",
        s.centro_estetico || "",
        formatDate(s.submitted_at)
      ];
      quiz.questions.forEach(q => {
        row.push(s.answers[q.question_id] || "");
      });
      return row;
    });
    
    // Create CSV content
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    
    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `risposte_test_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success("Export completato!");
  };

  const viewDetail = (submission) => {
    setSelectedSubmission(submission);
    setDetailDialogOpen(true);
  };

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Risposte Test di Valutazione
            </h1>
            <p className="text-muted-foreground">
              {submissions.length} test completati
            </p>
          </div>
          <Button
            variant="outline"
            className="rounded-full mt-4 md:mt-0"
            onClick={exportToCSV}
            disabled={submissions.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </Button>
        </div>

        {/* Search */}
        <Card className="rounded-2xl border-border/50 mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome, email o centro estetico..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-secondary rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredSubmissions.length > 0 ? (
          <div className="space-y-3">
            {filteredSubmissions.map((submission) => (
              <Card
                key={submission.submission_id}
                className="rounded-xl border-border/50 hover:border-primary/30 transition-colors cursor-pointer"
                onClick={() => viewDetail(submission)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium">{submission.user_name}</h4>
                        <Badge variant="outline" className="rounded-full text-xs">
                          <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                          Completato
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {submission.user_email}
                        </span>
                        {submission.centro_estetico && (
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {submission.centro_estetico}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(submission.submitted_at)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-medium mb-2">Nessuna risposta trovata</h3>
              <p className="text-muted-foreground text-sm">
                I test completati appariranno qui
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Risposte di {selectedSubmission?.user_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6">
              {/* User Info */}
              <Card className="rounded-xl bg-secondary/50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedSubmission.user_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedSubmission.user_email}</span>
                    </div>
                    {selectedSubmission.user_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedSubmission.user_phone}</span>
                      </div>
                    )}
                    {selectedSubmission.centro_estetico && (
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedSubmission.centro_estetico}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 col-span-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>Inviato il {formatDate(selectedSubmission.submitted_at)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Answers */}
              <div className="space-y-4">
                {quiz?.questions.map((question, index) => {
                  const answer = selectedSubmission.answers[question.question_id];
                  const isOpen = question.question_type === "open";
                  
                  return (
                    <div
                      key={question.question_id}
                      className="p-4 rounded-xl bg-secondary/30 border border-border/50"
                    >
                      <p className="font-medium text-sm mb-2">{question.question_text}</p>
                      <div className={`p-3 rounded-lg ${
                        isOpen ? "bg-blue-50 border-l-4 border-blue-400" : "bg-green-50 border-l-4 border-green-400"
                      }`}>
                        {isOpen ? (
                          <div className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-blue-500 mt-0.5" />
                            <p className="text-sm italic">{answer || "Non risposto"}</p>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">
                            {getAnswerText(question.question_id, answer) || "Non risposto"}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminQuizResponsesPage;
